import { Linkedin, GalleryHorizontalEnd, Code, Brain } from "lucide-react";
import { useScrollAnimation } from "../hooks/useScrollAnimation";
import ishaanJain from "../src/assets/ishaan_jain.webp";
import augustinaAlvarez from "../src/assets/agustina_alvarez.webp";
import mauriceMoran from "../src/assets/Maurice_Moran.webp";
import cillianMc from "../src/assets/cillianMc.webp";
import isabellaMc from "../src/assets/isabellaMc.webp";
import ihorK from "../src/assets/ihorK.webp";
import danielleU from "../src/assets/danielleU.webp";
import samMc from "../src/assets/samMc.webp";
interface Contributor {
  id: string;
  name: string;
  role: string;
  bio: string;
  image: string;
  backgroundColor: string;
  workHighlights: string[];
  socialLinks: {
    linkedin?: string;
    portfolio?: string;
  };
}

const contributors: Contributor[] = [
  {
    id: "ishaan-jain",
    name: "Ishaan Jain",
    role: "Website Developer",
    bio: "Ishaan is a Computer Science student at Trinity College Dublin and a full-stack developer based in Dublin, Ireland.",
    image: ishaanJain,
    backgroundColor: "bg-gradient-to-br from-amber-400 to-yellow-600",
    workHighlights: [
      "Tested React-Unity Plugin for VR integration.",
      "Developed AnamVR website using Typescript.",
      "Implemented modern frameworks for app and website.",
    ],
    socialLinks: {
      linkedin: "https://linkedin.com/in/ishaanj9",
      portfolio: "https://jaini.me",
    },
  },
  {
    id: "agustina-alvarez",
    name: "Agustina Alvarez",
    role: "Content Contributor",
    bio: "Agustina is a qualified and practicing Dialectical Behavioural Therapist based in Córdoba, Argentina.",
    image: augustinaAlvarez,
    backgroundColor: "bg-gradient-to-br from-green-500 to-emerald-600",
    workHighlights: [
      "Developed therapeutic content.",
      "Wrote several audio mediations.",
    ],
    socialLinks: {
      linkedin:
        "https://www.linkedin.com/in/agustina-alvarez-ulfohn-520922191/",
    },
  },
  {
    id: "maurice-moran",
    name: "Maurice Moran",
    role: "Sound Engineer",
    bio: "Maurice is Sound Engineer and Developer based out of Dublin, Ireland.",
    image: mauriceMoran,
    backgroundColor: "bg-gradient-to-br from-green-500 to-emerald-600",
    workHighlights: [
      "Created custom music for each VR scene.",
      "Helped create custom enviromental sounds and interactions for each scene, adding to the immersivity.",
    ],
    socialLinks: {
      linkedin: "https://www.linkedin.com/in/maurice-moran-254b94129/",
    },
  },
  {
    id: "cillian-mcpadden",
    name: "Cillian Mc Padden",
    role: "Unity Developer",
    bio: "Cillian is game developer student, based out of Dublin, Ireland.",
    image: cillianMc,
    backgroundColor: "bg-gradient-to-br from-green-500 to-emerald-600",
    workHighlights: [
      "Lead development on multiple VR scenes, including:",
      "Mountain View VR",
      "CropField VR",
    ],
    socialLinks: {
      linkedin: "https://www.linkedin.com/in/cillian-mc-padden-73b47b36b/",
      portfolio: "https://cillian6413.itch.io/",
    },
  },
  {
    id: "isabella-McKeever",
    name: "Isabella McKeever Scully",
    role: "2D & 3D Arist",
    bio: "Isabella is a 2D/3D artist & game design student based out of Dublin, Ireland.",
    image: isabellaMc,
    backgroundColor: "bg-gradient-to-br from-green-500 to-emerald-600",
    workHighlights: [
      "Lead Designer on multiple VR scenes, including:",
      "Animal Journey VR",
      "Snowy Cabin VR",
      "Oasis VR",
    ],
    socialLinks: {
      linkedin: "https://www.linkedin.com/in/isabella-mckeever-scully-bb20952a5/",
      portfolio: "https://linktr.ee/froggyivy",
    },
  },
  {
    id: "ihor-korovanenko",
    name: "Ihor Korovanenko",
    role: "Unity Developer",
    bio: "Ihor is game developer student, based out of Dublin, Ireland.",
    image: ihorK,
    backgroundColor: "bg-gradient-to-br from-green-500 to-emerald-600",
    workHighlights: [
      "Lead development on multiple VR scenes, including:",
      "Animal Journey VR",
      "Snowy Cabin VR",
      "Oasis VR",
    ],
    socialLinks: {
      linkedin: "https://www.linkedin.com/in/ihorkor/",
      portfolio: "https://saw3.itch.io/",
    },
  },
  {
    id: "daniele-uzpoleviciute",
    name: "Daniele Uzpoleviciute",
    role: "2D & 3D Arist",
    bio: "Daniele is a 2D/3D artist & game design student based out of Dublin, Ireland.",
    image: danielleU,
    backgroundColor: "bg-gradient-to-br from-green-500 to-emerald-600",
    workHighlights: [
      "Designer on mountain view VR",
      "2D designer responsible for hand drawn custom images throughout lessons",
    ],
    socialLinks: {
      linkedin: "https://www.linkedin.com/in/daniele-uzpoleviciute-3194a71a1/",
       portfolio: "https://linktr.ee/danniflowie",
    },
  },
  {
    id: "samuel-oreilly",
    name: "Sam O'Reilly",
    role: "3D Artist/Game Designer",
    bio: "Sam is a 3D artist & game design student based out of Ireland.",
    image: samMc,
    backgroundColor: "bg-gradient-to-br from-green-500 to-emerald-600",
    workHighlights: [
     "Lead Designer on multiple VR scenes, including:",
      "Mountain View VR",
      "Crop Field VR",
    ],
    socialLinks: {
      linkedin: "https://www.linkedin.com/in/samuel-o-reilly-2123b9255/",
      portfolio: "https://linktr.ee/shor02",
    },
  },
];

export default function ContributorsPage() {
  // Animation hooks for each contributor card
  const contributorAnimations = contributors.map((_, index) =>
    useScrollAnimation({ delay: index * 200 })
  );
  // Animation hook for the section wrapper
  const sectionAnimation = useScrollAnimation({ delay: 100 });
  return (
    <div
      ref={sectionAnimation.ref}
      className={`min-h-screen bg-gradient-to-br from-white via-purple-50 to-indigo-100 transition-opacity duration-700 ease-out ${
        sectionAnimation.isVisible ? "animate-slide-up" : "opacity-0"
      }`}
    >
      {/* Header */}
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="pt-40 pb-16 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-[#2b2b2b] mb-6">
            Our{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-purple-800">
              Contributors
            </span>
          </h1>
          <p className="text-lg sm:text-xl text-[#2b2b2b] max-w-4xl px-10 mx-auto leading-relaxed">
            Outside of AnamVR’s core team is a group of dedicated volunteers
            who’ve helped us build this platform from the ground up —
            contributing their time, skills, and passion to make it what it is
            today.
          </p>
          <p className="text-lg sm:text-xl py-4 text-[#2b2b2b] max-w-4xl px-10 mx-auto leading-relaxed">
            From content creation and testing to design feedback and community
            outreach, their input has shaped every part of the app. We’re proud
            to recognise their impact — and grateful to have them on the journey
            with us.
            <p className="text-base sm:text-lg text-[#2b2b2b] max-w-4xl mx-auto py-8 leading-relaxed">
              If you are interested in collaborating with us, please feel free
              to reach out to us at{" "}
              <a
                className="text-purple-500 underline hover:text-purple-700 transition-colors"
                href="mailto:hello@anamvr.com"
              >
                hello@anamvr.com
              </a>
            </p>
          </p>
        </div>
      </div>

      {/* Contributors Grid */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 px-2 sm:px-0 max-w-4xl mx-auto">
          {contributors.map((contributor, index) => (
            <div
              key={contributor.id}
              ref={contributorAnimations[index].ref}
              className={`group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden transform hover:-translate-y-2 h-full flex flex-col ${
                contributorAnimations[index].isVisible
                  ? "animate-slide-up"
                  : "opacity-0"
              }`}
            >
              {/* Image Section */}
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={contributor.image || "/placeholder.svg"}
                  alt={contributor.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
              </div>

              {/* Content Section */}
              <div className="p-6 flex flex-col justify-between h-full">
                <div className="flex-1 space-y-2">
                  <h3 className="text-2xl font-bold text-[#2b2b2b] mb-1">
                    {contributor.name}
                  </h3>
                  <p className="text-lg font-semibold text-[#7745b8] mb-3">
                    {contributor.role}
                  </p>
                  <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                    {contributor.bio}
                  </p>

                  {/* Work Highlights */}
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold mt-4 text-gray-800">
                      Key Contributions:
                    </h4>
                    {contributor.workHighlights.map((highlight, idx) => (
                      <div
                        key={idx}
                        className="flex items-start gap-2 text-sm text-gray-600"
                      >
                        <div className="w-1.5 h-1.5 bg-purple-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span>{highlight}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Bottom Social Links */}
                <div className="flex gap-3 mt-auto pt-4 border-t border-gray-100">
                  {contributor.socialLinks.linkedin && (
                    <a
                      href={contributor.socialLinks.linkedin}
                      target="_blank"
                      aria-label="LinkedIn Profile"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium"
                    >
                      <Linkedin className="w-4 h-4" />
                      LinkedIn
                    </a>
                  )}
                  {contributor.socialLinks.portfolio && (
                    <a
                      href={contributor.socialLinks.portfolio}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Portfolio Website"
                      className="flex items-center gap-2 px-3 py-2 bg-sky-50 text-sky-600 rounded-lg hover:bg-sky-100 transition-colors text-sm font-medium"
                    >
                      <GalleryHorizontalEnd className="w-4 h-4" />
                      Portfolio
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
